import { Layer, Marker, Popup, Source } from "react-map-gl";
import { getCachedAuthToken } from "../Utils/AuthUtils";
import type { LayerProps } from "react-map-gl";
import type { Feature } from "geojson";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { VehicleDisplay } from "../../models/VehicleDisplay";
import { VehicleState } from "../../models/VehicleState";
import { Button, Card } from "react-bootstrap";
import { getRestUrl } from "../../config";
import { usePlayback } from "../../context/PlaybackContext";

export const VehicleIcon: React.FC<{
  vehicleState: VehicleState,
  vehicleDisplay: VehicleDisplay
}> = (props) => {
  const navigate = useNavigate();
  const { dataSource } = usePlayback();
  const [token, setToken] = useState("");
  const [directionsGeometry, setDirectionsGeometry] = useState([]);

  const MPS_TO_MPH = 2.236936;

  // Load (and silently refresh) an access token
  useEffect(() => {
    let cancelled = false;

    async function loadToken() {
      if (token) return;

      try {
        const accessToken = await getCachedAuthToken();

        if (!accessToken) {
          console.error("No access token available. Route guard should have redirected to login.");
          return;
        }

        if (!cancelled) setToken(accessToken);
      } catch (error: any) {
        console.error("Error fetching token:", error?.message ?? error);
      }
    }

    loadToken();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {

    function fetchVehicleDirections() {
      if (!token || token.length === 0) {
        return;
      }

      try {
        // Get the latest VehicleStates
        const apiPath = dataSource === 'postgis' ? '/api/db-vehicle' : '/api/vehicle';
        const getStatesUrl = getRestUrl(`${apiPath}/get-vehicle-directions/${props.vehicleState.id}`);
        fetch(getStatesUrl, {
          method: 'get',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include'
        })
          .then(async response => response.json())
          .then(data => {
            const dg = data.routes[0].legs.flatMap((leg: any) =>
              leg.steps.map((step: any) => step.geometry.coordinates)
            );

            setDirectionsGeometry(dg);
          });

      }
      catch (error: any) {
        console.log(`Error caught in fetchVehicleDirections: ${error.message}`);
      }
    };
    fetchVehicleDirections();
  }, [props.vehicleState.id, token, dataSource]);

  function getLineLayer(id: string, colorCode: string, vehicleSize: number): LayerProps {
    return {
      id: `line-${id}`,
      type: 'line',
      paint: {
        'line-width': vehicleSize / 2.0,
        'line-color': `${colorCode}`,
        'line-opacity': 0.5
      }
    };
  }

  function getLineData(id: string, directionsGeometry: any): Feature {
    return {
      type: 'Feature',
      properties: {
        title: `line-${id}`
      },
      geometry: {
        type: 'MultiLineString',
        coordinates: directionsGeometry
      }
    };
  }

  let lineData = getLineData(props.vehicleState.id, directionsGeometry);
  let lineLayer = getLineLayer(props.vehicleState.id, props.vehicleState.colorCode, props.vehicleDisplay.size);
  let lineVisible = props.vehicleDisplay.routeVisible;
  let popupVisible = props.vehicleDisplay.popupVisible;
  let pxSize = Math.round(2.5 * props.vehicleDisplay.size) + "px";

  return (
    <>
      <Marker key={`point-{id}`}
        longitude={props.vehicleState.degLongitude}
        latitude={props.vehicleState.degLatitude}
        rotation={(props.vehicleState.degBearing + 180.0) % 360}>
        <svg id="triangle" viewBox="0 0 100 100" height={pxSize}>
          <polygon points="0 0 50 20 100 0 50 100" fill={props.vehicleState.colorCode}/>
        </svg>
      </Marker>
      {directionsGeometry && lineVisible && (
        <Source type='geojson' data={lineData}>
          <Layer {...lineLayer} />
        </Source>
      )}
      {popupVisible && (
        <Popup
          longitude={props.vehicleState.degLongitude}
          latitude={props.vehicleState.degLatitude}
          anchor="bottom-left">
          <Card.Body>
            <Card.Text>
              Vehicle: {props.vehicleState.id.substring(0, 8)}<br/>
              Speed: {(Math.round(MPS_TO_MPH * props.vehicleState.metersPerSecond * 10) / 10).toFixed(1)} MPH<br/>
              Bearing: {(Math.round(props.vehicleState.degBearing * 10) / 10).toFixed(1)}&deg;
            </Card.Text>
            <Button
              variant="primary"
              onClick={() => {
                const provider = localStorage.getItem('roadrunner_map_provider') || 'google';
                navigate(provider === 'google' ? `/google/driver-view/${props.vehicleState.id}` : `/driver-view/${props.vehicleState.id}`);
              }}
            >
              Jump into vehicle
            </Button>
          </Card.Body>
        </Popup>
      )}
    </>
  );
}
